"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import CommonBorderedRadioButton from "@/components/utils/radio/CommonBorderedRadioButton";
import CreateWaveVotingRep from "./CreateWaveVotingRep";
import CreateWaveVotingThreshold from "./CreateWaveVotingThreshold";
import MaxVotesPerIdentityInput from "./MaxVotesPerIdentityInput";
import NegativeVotingToggle from "./NegativeVotingToggle";
import TimeWeightedVoting from "./TimeWeightedVoting";
import type { TimeWeightedVotingConfig } from "./types";
const VOTING_TYPES_ORDER: Record<ApiWaveCreditType, number | undefined> = {
  [ApiWaveCreditType.TdhPlusXtdh]: 0,
  [ApiWaveCreditType.Tdh]: 1,
  [ApiWaveCreditType.Rep]: 2,
  [ApiWaveCreditType.Xtdh]: undefined,
  [ApiWaveCreditType.CardSetTdh]: undefined
};
const TIME_WEIGHTED_DURATION_ERROR = "This interval is longer than the wave duration. Choose a shorter interval, extend the wave end date, or clear the end date.";
const VOTING_SETTINGS_GRID_CLASSES = "tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-3 tw-border-t tw-border-iron-700 tw-pt-6";
export default function CreateWaveVoting(t0) {
  const $ = _c(43);
  const {
    waveType,
    selectedType,
    category,
    profileId,
    maxVotesPerIdentityPerDrop,
    approvalThreshold,
    errors,
    onTypeChange,
    setCategory,
    setProfileId,
    setMaxVotesPerIdentityPerDrop,
    setApprovalThreshold,
    timeWeighted,
    onTimeWeightedChange
  } = t0;
  const [allowNegativeVotes, setAllowNegativeVotes] = useState(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      [ApiWaveType.Chat]: "How Drops are Rated",
      [ApiWaveType.Rank]: "How Drops are Voted",
      [ApiWaveType.Approve]: "How Drops are Voted"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const TITLES = t1;
  if (selectedType === null) {
    return null;
  }
  const timeWeightedErrorMessage = errors.includes(CREATE_WAVE_VALIDATION_ERROR.TIME_WEIGHTED_VOTING_INTERVAL_EXCEEDS_WAVE_DURATION) ? TIME_WEIGHTED_DURATION_ERROR : undefined;
  let t2;
  if ($[1] !== errors) {
    t2 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.APPROVAL_THRESHOLD_REQUIRED);
    $[1] = errors;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const approvalThresholdError = t2;
  const showVotingSettings = waveType !== ApiWaveType.Chat;
  const t3 = TITLES[waveType];
  let t4;
  if ($[3] !== t3) {
    t4 = <p className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-50 sm:tw-text-xl">{t3}</p>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = Object.keys(VOTING_TYPES_ORDER);
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (t5 as ApiWaveCreditType[]).filter(_temp);
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== onTypeChange || $[8] !== selectedType) {
    t7 = t6.map(votingType_0 => <CommonBorderedRadioButton key={votingType_0} type={votingType_0} selected={selectedType} disabled={false} variant="subtle" onChange={onTypeChange}><span className={`tw-flex tw-min-h-4 tw-items-center tw-text-sm tw-font-semibold ${selectedType === votingType_0 ? "tw-text-white" : "tw-text-iron-300 group-hover:tw-text-white"}`}>{`By ${WAVE_VOTING_LABELS[votingType_0]}`}</span></CommonBorderedRadioButton>);
    $[7] = onTypeChange;
    $[8] = selectedType;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== category || $[11] !== errors || $[12] !== profileId || $[13] !== selectedType || $[14] !== setCategory || $[15] !== setProfileId) {
    t8 = selectedType === ApiWaveCreditType.Rep && <div className="tw-col-span-full"><CreateWaveVotingRep category={category} profileId={profileId} errors={errors} setCategory={setCategory} setProfileId={setProfileId} /></div>;
    $[10] = category;
    $[11] = errors;
    $[12] = profileId;
    $[13] = selectedType;
    $[14] = setCategory;
    $[15] = setProfileId;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <div className="tw-mt-3 tw-grid tw-gap-x-4 tw-gap-y-4 lg:tw-grid-cols-3">{t7}{t8}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== approvalThreshold || $[21] !== approvalThresholdError || $[22] !== errors || $[23] !== maxVotesPerIdentityPerDrop || $[24] !== setApprovalThreshold || $[25] !== setMaxVotesPerIdentityPerDrop || $[26] !== showVotingSettings || $[27] !== waveType) {
    t10 = showVotingSettings && <div data-testid="create-wave-voting-settings-grid" className={VOTING_SETTINGS_GRID_CLASSES}><MaxVotesPerIdentityInput value={maxVotesPerIdentityPerDrop} errors={errors} onChange={setMaxVotesPerIdentityPerDrop} />{waveType === ApiWaveType.Approve && <CreateWaveVotingThreshold threshold={approvalThreshold} error={approvalThresholdError} setThreshold={setApprovalThreshold} />}</div>;
    $[20] = approvalThreshold;
    $[21] = approvalThresholdError;
    $[22] = errors;
    $[23] = maxVotesPerIdentityPerDrop;
    $[24] = setApprovalThreshold;
    $[25] = setMaxVotesPerIdentityPerDrop;
    $[26] = showVotingSettings;
    $[27] = waveType;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== allowNegativeVotes || $[30] !== waveType) {
    t11 = waveType !== ApiWaveType.Chat && <NegativeVotingToggle allowNegativeVotes={allowNegativeVotes} onChange={setAllowNegativeVotes} />;
    $[29] = allowNegativeVotes;
    $[30] = waveType;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== onTimeWeightedChange || $[33] !== timeWeighted || $[34] !== timeWeightedErrorMessage || $[35] !== waveType) {
    t12 = waveType !== ApiWaveType.Chat && <TimeWeightedVoting config={timeWeighted} errorMessage={timeWeightedErrorMessage} onChange={onTimeWeightedChange} />;
    $[32] = onTimeWeightedChange;
    $[33] = timeWeighted;
    $[34] = timeWeightedErrorMessage;
    $[35] = waveType;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t12 || $[40] !== t4 || $[41] !== t9) {
    t13 = <div>{t4}{t9}{t10}{t11}{t12}</div>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t4;
    $[41] = t9;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
}
function _temp(votingType) {
  return VOTING_TYPES_ORDER[votingType] !== undefined;
}